import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiGet } from '../config/api';

const UsageContext = createContext(null);

export function UsageProvider({ children }) {
  const [usage, setUsage] = useState(null);
  const [toasts, setToasts] = useState([]);

  const refreshUsage = useCallback(async () => {
    try {
      const data = await apiGet('/api/nurse/usage');
      setUsage(data);
      const next = data.notifications || [];
      if (next.length) {
        setToasts((prev) => [...prev, ...next.map((n, i) => ({ ...n, id: `${Date.now()}-${i}` }))]);
      }
      return data;
    } catch {
      return null;
    }
  }, []);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    refreshUsage();
  }, [refreshUsage]);

  useEffect(() => {
    const timers = toasts
      .filter((t) => t.auto_dismiss_seconds)
      .map((t) => setTimeout(() => dismiss(t.id), t.auto_dismiss_seconds * 1000));
    return () => timers.forEach(clearTimeout);
  }, [toasts, dismiss]);

  const value = useMemo(
    () => ({ usage, toasts, refreshUsage, dismiss, blockMic: Boolean(usage?.block_recording) }),
    [usage, toasts, refreshUsage, dismiss]
  );
  return <UsageContext.Provider value={value}>{children}</UsageContext.Provider>;
}

export function useUsage() {
  return useContext(UsageContext);
}

export function UsageToasts() {
  const ctx = useUsage();
  if (!ctx) return null;
  return (
    <div className="pointer-events-none fixed left-0 right-0 top-3 z-[200] flex flex-col items-center gap-2 px-4">
      {ctx.toasts.map((toast) => (
        <div
          key={toast.id}
          className={`pointer-events-auto flex w-full max-w-lg items-start gap-3 rounded-xl px-4 py-3 text-sm text-white shadow-lg ${
            toast.color === 'red' ? 'bg-red-600' : toast.color === 'orange' ? 'bg-orange-500' : 'bg-blue-600'
          }`}
        >
          <span className="flex-1">{toast.message}</span>
          <button type="button" className="text-white/90" onClick={() => ctx.dismiss(toast.id)} aria-label="Dismiss">
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
